import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { DevCookieService } from '../dev-cookie/dev-cookie.service';
import { ConfigurationService } from 'src/services/configuration/configuration.service';
import { LoggerService } from '../../logger-services/logger.service';
import {
  DevAuthRequest,
  DevAuthResponse,
  DevCredentialsAuthRequest,
  CookieConfig,
  UserInfo,
  TokenInfo,
} from '../../../app/models/auth.models';
import { TokenService } from '../../../shared/services/token/token.service';

@Injectable({
  providedIn: 'root',
})
export class DevAuthService {
  private readonly COMMON_AUTH_ENDPOINT ='/v2/api/auth_login';


  private readonly DevAuthServiceError = (error: any) =>
    `[DevAuthService] - ${JSON.stringify(error)}`;

  constructor(
    private readonly http: HttpClient,
    private readonly cookieService: DevCookieService,
    private readonly configService: ConfigurationService,
    private readonly loggerService: LoggerService,
    private readonly tokenService: TokenService
  ) {}

  /**
   * Check if we're in development environment
   */
  private isDevelopmentEnvironment(): boolean {
    const config = this.configService.config;
    if (config?.ENVIRONMENT) {
      return config.ENVIRONMENT === 'development' || config.ENVIRONMENT === 'dev';
    }
    
    // Default fallback - assume dev if localhost
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }

  /**
   * Get API base URL from config
   */
  private getApiBaseUrl(): string {
    const config = this.configService.config;
    // For dev authentication, use apiBaseUrl since it now points to the actual API server
    return config?.apiBaseUrl || 'https://devapi-chatbot.freyrapps.com';
  }

  /**
      * Authenticate using username and password via chatbot-common `auth_login`.
   */
  authenticateWithCredentials(username: string, password: string): Observable<DevAuthResponse> {
    if (!this.isDevelopmentEnvironment()) {
      return throwError(() => new Error('Dev authorization not available in this environment'));
    }

    const body: DevCredentialsAuthRequest = { username, password };

    return this.http.post<DevAuthResponse>(this.getApiBaseUrl() + this.COMMON_AUTH_ENDPOINT, body).pipe(
      mergeMap((response) => {
        if (response?.success === false) {
          const message = response.message || 'Login failed';
          return throwError(() => ({ error: { message } }));
        }
        const data = response?.data;
        if (!data?.accessToken) {
          const message = response?.message || 'Login failed';
          return throwError(() => ({ error: { message } }));
        }
        this.setAuthCookies(response);
        return of(response);
      }),
      catchError((error) => {
        this.loggerService.logError(this.DevAuthServiceError(error), 'authenticateWithCredentials');
        const normalized = this.normalizeCredentialLoginError(error, username);
        return throwError(() => normalized);
      })
    );
  }

  /** Parsed from `detail.error` JSON string on 403 AuthenticationFailed. */
  private tryParseAuthFailedPayload(detailError: string): {
    remainingPasswordAttempts: number;
    maxPasswordAttempts: number;
  } | null {
    try {
      const parsed = JSON.parse(detailError) as {
        remainingPasswordAttempts?: number;
        maxPasswordAttempts?: number;
      };
      const remaining = parsed.remainingPasswordAttempts;
      if (typeof remaining !== 'number') {
        return null;
      }
      const max =
        typeof parsed.maxPasswordAttempts === 'number' ? parsed.maxPasswordAttempts : 5;
      return { remainingPasswordAttempts: remaining, maxPasswordAttempts: max };
    } catch {
      return null;
    }
  }

  private formatAuthFailed403Message(detail: {
    error?: string | null;
    error_type?: string | null;
  }): string | null {
    if (detail.error_type !== 'AuthenticationFailed' || typeof detail.error !== 'string') {
      return null;
    }
    const parsed = this.tryParseAuthFailedPayload(detail.error);
    if (!parsed) {
      return null;
    }
    const { remainingPasswordAttempts, maxPasswordAttempts } = parsed;
    return (
      `Incorrect password.`+
      ` ${remainingPasswordAttempts} attempts remaining. ` +
      `Your account will be locked after ${maxPasswordAttempts} consecutive failed attempts.`
    );
  }

  private isWrappedLoginClientError(error: unknown): boolean {
    return (
      !!error &&
      typeof error === 'object' &&
      'error' in error &&
      typeof (error as { error?: { message?: unknown } }).error === 'object' &&
      typeof (error as { error: { message?: unknown } }).error?.message === 'string'
    );
  }

  /**
   * Maps 403/404 auth_login bodies to user-facing copy; otherwise returns the original error.
   */
  private normalizeCredentialLoginError(error: unknown, usernameInput: string): unknown {
    if (this.isWrappedLoginClientError(error)) {
      return error;
    }

    if (error instanceof HttpErrorResponse) {
      const body = error.error as Record<string, unknown> | null | undefined;
      const detail = body?.['detail'] as Record<string, unknown> | undefined;

      if (error.status === 403 && detail) {
        const msg = this.formatAuthFailed403Message({
          error: detail['error'] as string | null | undefined,
          error_type: detail['error_type'] as string | null | undefined,
        });
        if (msg) {
          return { error: { message: msg } };
        }
      }

      if (
        error.status === 404 &&
        typeof detail?.['error'] === 'string' &&
        (detail['error'] as string).includes('User does not exist')
      ) {
        return {
          error: {
            message: `Account with userEmail ${usernameInput.trim()} not found.`,
          },
        };
      }
    }

    return error;
  }


  /**
   * Set authentication cookies based on API response
   */
  private setAuthCookies(authResponse: DevAuthResponse): void {
    const baseDomain = this.extractBaseDomain();
    const data = authResponse.data;

    // Calculate expiration dates
    const accessTokenExpires = this.calculateExpirationDate(parseInt(data.accessTokenExpiresIn));
    const refreshTokenExpires = this.calculateExpirationDate(parseInt(data.refreshTokenExpiresIn));
    const sessionExpires = this.calculateExpirationDate(parseInt(data.sessionExpiresIn));

    // Set only the cookies that should appear in the browser
    // Set ACCESS_TOKEN cookie
    this.setAuthCookie('ACCESS_TOKEN', data.accessToken, baseDomain, accessTokenExpires);

    // Set REFRESH_TOKEN cookie
    this.setAuthCookie('REFRESH_TOKEN', data.refreshToken, baseDomain, refreshTokenExpires);

    // Set USER_ID cookie
    this.setAuthCookie('USER_ID', data.userId, baseDomain, sessionExpires);

    // Set USER_EMAIL cookie
    this.setAuthCookie('USER_EMAIL', data.email, baseDomain, sessionExpires);

    // Set USER_NAME cookie (using username from API)
    this.setAuthCookie('USER_NAME', data.username, baseDomain, sessionExpires);

        // USER_TENANT_ID is always forced to "0" regardless of the API response
    this.setAuthCookie('USER_TENANT_ID', "0", baseDomain, sessionExpires);

    // USER_PRIVILEGES must use URF-format codes for the dev privilege system.
    // The login API returns Cognito role types (e.g. "Common User") which the app cannot
    // process directly — in production a separate GET_FREYA_PRIVILEGES call bridges this gap,
    // but that call is skipped when IsDevelopment=true.  We therefore override with the
    // URF privilege codes defined in environment.development.ts DEV_OVERRIDES.
    const urfPrivileges = [
      'view_access_to_ra_mpr',
      'view_access_to_regulations_mpr',
      'view_access_to_racsclaims_mpr',
      'view_access_to_racsprobiotic_mpr',
      'view_access_to_racsstandard_mpr',
      'view_access_to_rtq_mpr',
      'gri.regulation.read',
      'workbench_access'
    ];
    this.setAuthCookie('USER_PRIVILEGES', JSON.stringify(urfPrivileges), baseDomain, sessionExpires);

    // Set USER_INFO cookie (using idToken from API)
    this.setAuthCookie('USER_INFO', data.idToken, baseDomain, sessionExpires);

    // Set DOMAIN cookie (default to MPR)
    this.setAuthCookie('DOMAIN', 'MPR', baseDomain, sessionExpires);

    // Set LOGIN_DOMAIN cookie (extract from current hostname or use default)
    const loginDomain = window.location.hostname.includes('devsecops') ? 'devsecops.freyafusion.com' : 'devsecops.freyafusion.com';
    this.setAuthCookie('LOGIN_DOMAIN', loginDomain, baseDomain, sessionExpires);
  }

  /**
   * Set a single authentication cookie
   */
  private setAuthCookie(name: string, value: string, domain: string, expires: Date): void {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    const cookieConfig: CookieConfig = {
      name,
      value,
      domain: isLocalhost ? undefined : domain, // Don't set domain for localhost
      path: '/',
      expires,
      secure: !isLocalhost, // Only secure for non-localhost
      httpOnly: false,
      sameSite: 'lax',
    };
    this.cookieService.setCookie(cookieConfig);
  }

  /**
   * Extract base domain from current location
   */
  private extractBaseDomain(): string {
    const hostname = window.location.hostname;

    // For development domains like .devapi-chatbot.freyrapps.com
    if (hostname.includes('.dev')) {
      return '.devapi-chatbot.freyrapps.com';
    }

    // For localhost development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'localhost';
    }

    // Default fallback
    return hostname;
  }

  /**
   * Calculate expiration date from expires_in seconds
   */
  private calculateExpirationDate(expiresIn?: number): Date {
    if (expiresIn) {
      return new Date(Date.now() + expiresIn * 1000);
    }

    // Default to 24 hours if no expiration provided
    return new Date(Date.now() + 24 * 60 * 60 * 1000);
  }
    /** JWT `exp` claim as Unix seconds, or null if not a decodable JWT. */
  private parseJwtExpirationUnix(accessToken: string): number | null {
    try {
      const parts = accessToken.split('.');
      if (parts.length < 2) {
        return null;
      }
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const pad = (4 - (base64.length % 4)) % 4;
      const padded = base64 + '='.repeat(pad);
      const json = JSON.parse(atob(padded)) as { exp?: unknown };
      return typeof json.exp === 'number' ? json.exp : null;
    } catch {
      return null;
    }
  }

  /**
   * True if the token has a JWT `exp` that is in the past (with skew).
   * Empty token = expired. Opaque tokens (no `exp`) are treated as not expired client-side.
   */
  private isAccessTokenExpired(accessToken: string): boolean {
    if (!accessToken.trim()) {
      return true;
    }
    const exp = this.parseJwtExpirationUnix(accessToken);
    if (exp == null) {
      return false;
    }
    const nowSec = Math.floor(Date.now() / 1000);
    const skewSec = 60;
    return nowSec >= exp - skewSec;
  }


  /**
   * Get user information from cookies
   */
  getUserInfo(): UserInfo | null {
    if (!this.isDevelopmentEnvironment()) {
      return null;
    }

    const userId = this.cookieService.getCookie('USER_ID');
    const email = this.cookieService.getCookie('USER_EMAIL');
    const username = this.cookieService.getCookie('USER_NAME');
    const privilegesStr = this.cookieService.getCookie('USER_PRIVILEGES');
    const tenantId = this.cookieService.getCookie('USER_TENANT_ID');

    if (!userId || !email) {
      return null;
    }

    return {
      userId,
      email,
      username: username || email,
      firstName: '', // Not available in our cookie set
      lastName: '', // Not available in our cookie set
      name: username || email,
      privileges: privilegesStr ? JSON.parse(privilegesStr) : [],
      tenantId: tenantId || '0',
      authMechanism: '', // Not available in our cookie set
      roleTypeList: privilegesStr ? JSON.parse(privilegesStr) : [],
    };
  }

  /**
   * Get token information from cookies
   */
  getTokenInfo(): TokenInfo | null {
    if (!this.isDevelopmentEnvironment()) {
      return null;
    }

    const accessToken = this.cookieService.getCookie('ACCESS_TOKEN');
    const idToken = this.cookieService.getCookie('ID_TOKEN');
    const refreshToken = this.cookieService.getCookie('REFRESH_TOKEN');
    const sessionExpiresIn = this.cookieService.getCookie('SESSION_EXPIRES_IN');
    const accessTokenExpiresIn = this.cookieService.getCookie('ACCESS_TOKEN_EXPIRES_IN');
    const refreshTokenExpiresIn = this.cookieService.getCookie('REFRESH_TOKEN_EXPIRES_IN');

    if (!accessToken) {
      return null;
    }

    return {
      accessToken,
      idToken: idToken || '',
      refreshToken: refreshToken || '',
      sessionExpiresIn: sessionExpiresIn || '',
      accessTokenExpiresIn: accessTokenExpiresIn || '',
      refreshTokenExpiresIn: refreshTokenExpiresIn || '',
      tokenType: 'Bearer',
    };
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    if (!this.isDevelopmentEnvironment()) {
      return false;
    }

    return this.cookieService.hasCookie('ACCESS_TOKEN') && this.cookieService.hasCookie('USER_ID');
  }

  /**
   * Clear all authentication cookies
   */
  clearAuthCookies(): void {
    if (!this.isDevelopmentEnvironment()) {
      return;
    }

    const baseDomain = this.extractBaseDomain();

    this.cookieService.deleteCookie('ACCESS_TOKEN', baseDomain);
    this.cookieService.deleteCookie('ID_TOKEN', baseDomain);
    this.cookieService.deleteCookie('REFRESH_TOKEN', baseDomain);
    this.cookieService.deleteCookie('USER_ID', baseDomain);
    this.cookieService.deleteCookie('USER_EMAIL', baseDomain);
    this.cookieService.deleteCookie('USERNAME', baseDomain);
    this.cookieService.deleteCookie('FIRST_NAME', baseDomain);
    this.cookieService.deleteCookie('LAST_NAME', baseDomain);
    this.cookieService.deleteCookie('USER_TENANT_ID', baseDomain);
    this.cookieService.deleteCookie('ROLE_TYPE_LIST', baseDomain);
    this.cookieService.deleteCookie('AUTH_MECHANISM', baseDomain);
    this.cookieService.deleteCookie('TENANT_INFO_LIST', baseDomain);
    this.cookieService.deleteCookie('SESSION_EXPIRES_IN', baseDomain);
    this.cookieService.deleteCookie('ACCESS_TOKEN_EXPIRES_IN', baseDomain);
    this.cookieService.deleteCookie('REFRESH_TOKEN_EXPIRES_IN', baseDomain);
  }

  /**
   * Get access token for API calls
   */
  getAccessToken(): string | null {
    if (!this.isDevelopmentEnvironment()) {
      return null;
    }

    return this.cookieService.getCookie('ACCESS_TOKEN');
  }

  /**
   * Check if authentication is already completed (for app startup)
   */
  isAlreadyAuthenticated(): boolean {
    if (!this.isDevelopmentEnvironment()) {
      return false;
    }

    // Check if we have the essential cookies (only the ones we actually set)
    const hasAccessToken = this.cookieService.hasCookie('ACCESS_TOKEN');
    const hasUserId = this.cookieService.hasCookie('USER_ID');
    const hasUserName = this.cookieService.hasCookie('USER_NAME');

    return hasAccessToken && hasUserId && hasUserName;
  }

  /**
   * Dev session is usable: required auth cookies present and access token not JWT-expired.
   */
  hasActiveDevSession(): boolean {
    if (!this.isDevelopmentEnvironment()) {
      return false;
    }
    if (!this.isAlreadyAuthenticated()) {
      return false;
    }
    const token = this.getAccessToken();
    if (token == null || token === '') {
      return false;
    }
    return !this.isAccessTokenExpired(token);
  }


  /**
   * Update a specific cookie with an overridden value (for browser inspect visibility)
   */
  private updateCookieWithOverride(cookieName: string, value: string): void {
    const baseDomain = this.extractBaseDomain();
    const expires = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours
    this.setAuthCookie(cookieName, value, baseDomain, expires);
  }

  /**
   * Update environment with cookie values - now works with reactive environment system
   * The new environment.development.ts automatically reads from cookies, so we just need to
   * handle selective overrides and trigger update events
   */
  updateEnvironmentWithCookies(): void {
    if (!this.isDevelopmentEnvironment()) {
      return;
    }

    // Check if selective overrides are enabled
    import('../../../environments/environment').then((env) => {
      const environment = env.environment as any;
            
      // USER_TENANT_ID: always forced to override value
      this.updateCookieWithOverride('USER_TENANT_ID', environment.DEV_OVERRIDES.userTenantId);
      // USER_PRIVILEGES: forced to URF-format codes (login API returns Cognito role types
      // which are not processable directly in dev mode — GET_FREYA_PRIVILEGES is skipped)
      this.updateCookieWithOverride('USER_PRIVILEGES', JSON.stringify(environment.DEV_OVERRIDES.userPrivileges));

      // Trigger a custom event to notify components that user data is available
      // The environment getters will automatically reflect current cookie values
      const userDataEvent = new CustomEvent('userDataUpdated', {
        detail: {
          accessToken: this.tokenService.getAccessToken(),
          userEmail: environment.LoginUserName,
          userId: environment.LoginUserID,
          tenantId: environment.TenantId,
          domain: environment.DomainName,
          userPrivileges: environment.Privileges,
          overridesEnabled: environment.ENABLE_OVERRIDES
        }
      });
      window.dispatchEvent(userDataEvent);
    });

  }
}
